import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSession } from "../auth/session"
import useOnlineStatus from "../hooks/useOnlineStatus"
import { indexSnapshot, snapshotThumbnailUrls } from "../models/snapshot"
import { isStale } from "../models/syncStatus"
import { activityRequest } from "../network/activity"
import { flagIssueRequest } from "../network/flag"
import { fetchMenuSnapshot } from "../network/snapshot"
import { clearOfflineData as clearDeviceData } from "./clearOfflineData"
import { IMAGE_CACHE_STATE, precacheThumbnails, pruneThumbnails } from "./imageCache"
import { MenuDataContext } from "./menuData"
import { OUTBOX_KIND, enqueue, flushOutbox, pendingCounts, subscribeOutbox } from "./outbox"
import * as snapshotStore from "./snapshotStore"
import MenuSyncEngine, { INITIAL_SYNC_STATE } from "./syncEngine"

// Relative labels such as "synced 5m ago" re-render on this beat.
const CLOCK_TICK_MS = 60 * 1000
const IDLE_IMAGES = { saved: 0, total: 0, state: IMAGE_CACHE_STATE.IDLE }

// The one place the menu is read from: the device copy, kept current in the background.
export function MenuDataProvider({ children }) {
  const { isAuthenticated, user, restaurant, language } = useSession()
  const restaurantId = restaurant?.id ?? null
  const isActive = Boolean(isAuthenticated && user?.isRestaurantAdmin && restaurantId)
  const key = isActive ? snapshotStore.snapshotKey(restaurantId, language) : null

  const [record, setRecord] = useState(null)
  const [sync, setSync] = useState(INITIAL_SYNC_STATE)
  const [imageProgress, setImageProgress] = useState(IDLE_IMAGES)
  const [pending, setPending] = useState({ total: 0, flags: 0 })
  const [now, setNow] = useState(() => Date.now())
  const [resetCount, setResetCount] = useState(0)
  const engineRef = useRef(null)
  const isOnline = useOnlineStatus()

  useEffect(() => {
    setRecord(null)
    setSync(INITIAL_SYNC_STATE)
    setImageProgress(IDLE_IMAGES)
    if (!key) return undefined

    let isDisposed = false
    const engine = new MenuSyncEngine({
      key,
      restaurantId,
      language,
      store: snapshotStore,
      fetchSnapshot: fetchMenuSnapshot,
      onRecord: (next) => {
        if (!isDisposed) setRecord(next)
      },
      onState: (next) => {
        if (!isDisposed) setSync(next)
      },
      onSynced: () => {
        if (!isDisposed) flushOutbox()
      }
    })
    engineRef.current = engine
    engine.start()

    return () => {
      isDisposed = true
      engine.stop()
      if (engineRef.current === engine) engineRef.current = null
    }
  }, [key, restaurantId, language, resetCount])

  const menu = useMemo(() => {
    if (!record?.snapshot) return null
    try {
      return indexSnapshot(record.snapshot)
    } catch {
      return null
    }
  }, [record])

  // Saves thumbnails for the current menu and drops ones it no longer uses.
  useEffect(() => {
    if (!menu) return undefined
    let isStopped = false
    const urls = snapshotThumbnailUrls(menu)
    pruneThumbnails(urls).then(() =>
      precacheThumbnails(urls, {
        onProgress: (progress) => {
          if (!isStopped) setImageProgress(progress)
        },
        shouldStop: () => isStopped
      })
    )
    return () => {
      isStopped = true
    }
  }, [menu, isOnline])

  useEffect(() => {
    let isCurrent = true
    const refresh = () =>
      pendingCounts().then((counts) => {
        if (isCurrent) setPending(counts)
      })
    refresh()
    const unsubscribe = subscribeOutbox(refresh)
    return () => {
      isCurrent = false
      unsubscribe()
    }
  }, [])

  // Queued requests go out whenever there is a session and a chance of a connection.
  useEffect(() => {
    if (!isActive) return undefined
    const flush = () => flushOutbox()
    const flushWhenVisible = () => {
      if (document.visibilityState === "visible") flushOutbox()
    }
    flush()
    window.addEventListener("online", flush)
    document.addEventListener("visibilitychange", flushWhenVisible)
    return () => {
      window.removeEventListener("online", flush)
      document.removeEventListener("visibilitychange", flushWhenVisible)
    }
  }, [isActive])

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS)
    return () => clearInterval(timer)
  }, [])

  const syncNow = useCallback(() => engineRef.current?.syncNow({ reason: "manual" }), [])

  const clearOfflineData = useCallback(async () => {
    engineRef.current?.stop()
    await clearDeviceData()
    setResetCount((count) => count + 1)
  }, [])

  const queueActivity = useCallback(
    (activityType) => {
      if (!isActive) return
      enqueue({
        kind: OUTBOX_KIND.ACTIVITY,
        restaurantId,
        request: activityRequest(activityType)
      }).then(() => flushOutbox())
    },
    [isActive, restaurantId]
  )

  const queueFlag = useCallback(
    async (flag) => {
      await enqueue({ kind: OUTBOX_KIND.FLAG, restaurantId, request: flagIssueRequest(flag) })
      flushOutbox()
    },
    [restaurantId]
  )

  const value = useMemo(
    () => ({
      menu,
      sync,
      isOnline,
      now,
      isStale: isStale(sync.lastSyncedAt, now),
      imageProgress,
      pending,
      syncNow,
      clearOfflineData,
      queueActivity,
      queueFlag
    }),
    [menu, sync, isOnline, now, imageProgress, pending, syncNow, clearOfflineData, queueActivity, queueFlag]
  )

  return <MenuDataContext.Provider value={value}>{children}</MenuDataContext.Provider>
}
